import {
  FIXED_PAGES,
  REPEATABLE_PAGES,
  closedKey,
  isFixed,
  labelKey,
  navKey,
  publicKey,
  tileKey,
} from "../pages/registry";
import type { SitePage } from "../pages/registry";
import { useState } from "react";
import { useSitePages } from "../pages/nav";
import { isClosed } from "../pages/closedPages";
import {
  instancesKey,
  isAdded,
  nextInstance,
  readInstances,
  writeInstances,
} from "../pages/repeatable";
import {
  PAGES_ORDER_KEY,
  movable,
  movePage,
  writePageOrder,
} from "../pages/order";
import { Provenance } from "./Provenance";
import { useSettings } from "../settings/context";
import { Icon } from "../ui/Icon";

/**
 * LES PAGES DU SITE — une ligne par page : dans le menu ? en tuile sur l'accueil ?
 *
 * ⚠️ PORTÉ DU PROTOTYPE, jusqu'aux noms de classes (`.pgm`, `.pgm__h`, `.pgm__r`,
 * `.pgm__n`, `.pgm__c`), parce que ces noms sont un contrat : la trappe CSS vendue
 * au client est écrite contre eux. Son commentaire dit pourquoi ce gestionnaire
 * existe : « Remplace deux listes d'interrupteurs qui se répétaient (Navigation +
 * Tuiles). La question de l'organisateur n'est pas "quel flag ?" mais "où apparaît
 * cette page ?" : on y répond sur une seule ligne. »
 *
 * ⚠️ DEUX BOUTONS-INTERRUPTEURS, PAS DES CASES, et la MÊME COCHE dans les deux
 * colonnes — c'est le choix du prototype : l'en-tête de colonne dit laquelle est
 * laquelle, la coche ne dit que « oui ». `aria-pressed` porte l'état, et
 * `aria-label` répète la destination pour qui n'a pas l'en-tête sous les yeux.
 *
 * Le NOM de la page ne se règle pas ici, comme dans le prototype : il vit sur la
 * page elle-même (onglet « Cette page »), il est cascadé, donc traduisible et
 * surchargeable par typologie. Tant qu'il n'est pas écrit, c'est celui du registre.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function PagesManager() {
  const { value, write, detail, saving, scope } = useSettings();
  const { ordered } = useSitePages();
  const [refusal, setRefusal] = useState<string | null>(null);

  const onMovePage = (index: number, direction: -1 | 1) => {
    const moved = movePage(ordered, index, direction);
    if (!moved.pages) return setRefusal(moved.refusal);
    setRefusal(null);
    write(PAGES_ORDER_KEY, writePageOrder(moved.pages));
  };

  /**
   * ═══ LES PAGES MULTIPLIABLES S'AJOUTENT ICI ═══
   *
   * « On peut en ajouter autant que l'on veut sur l'accueil », pour les sections
   * comme pour les pages formulaire. Le plus petit rang libre, jamais l'heure ni le
   * hasard : c'est ce qui permet de promettre que retirer n'efface rien : remettre
   * une page retrouve le rang, donc les clés, donc le contenu qu'elle portait.
   *
   * ⚠️ CHAQUE FAMILLE COMPTE POUR ELLE-MÊME. Une liste commune aurait fait sauter la
   * section 2 en retirant le formulaire 2 : « 2 » ne dit pas de quelle page il parle.
   *
   * ⚠️ ELLE ARRIVE ÉTEINTE, comme toute page de contenu : ajouter n'est pas publier.
   * On écrit son contenu (le sélecteur d'écran, en haut, y mène), PUIS on l'allume ;
   * l'inverse montrerait aux participants une page que personne n'a préparée.
   */
  const addPage = (base: SitePage) => {
    const instances = readInstances(value(instancesKey(base)));
    write(
      instancesKey(base),
      writeInstances([...instances, nextInstance(instances)]),
    );
  };
  const removePage = (baseKey: string, instance: string) => {
    const base = REPEATABLE_PAGES.find((p) => p.key === baseKey);
    if (!base) return;
    const instances = readInstances(value(instancesKey(base)));
    write(
      instancesKey(base),
      writeInstances(instances.filter((i) => i !== instance)),
    );
  };

  /**
   * ⚠️ LE NOM D'UNE ÉTAPE SERVIE EN PAGE VIENT DE L'ÉTAPE (`nameKey`), pas d'un
   * `PAGE_<CLÉ>_LABEL` à elle. Deux mots pour une seule chose, et renommer
   * « Informations » dans le parcours aurait laissé le menu dire l'ancien.
   */
  const name = (page: SitePage) => {
    const written = value(page.nameKey ?? labelKey(page));
    return typeof written === "string" && written.trim() ? written : page.name;
  };

  /**
   * L'état EFFECTIF, clé absente comprise — et le défaut DIFFÈRE :
   *
   *   — page de contenu : éteinte tant qu'on ne l'allume pas. Un site qui sortirait
   *     avec toutes ses pages ouvertes montrerait des écrans que personne n'a préparés.
   *   — accueil et inscription : allumés tant qu'on ne les éteint pas. L'inverse
   *     aurait vidé le menu de tous les événements existants au prochain chargement.
   *
   * La tuile, elle, s'allume toujours explicitement : l'accueil porte déjà le bouton
   * d'inscription, une tuile de plus ne s'invite pas.
   */
  const on = (page: SitePage, key: string) =>
    isFixed(page.key) && key === navKey(page)
      ? value(key) !== false
      : value(key) === true;

  return (
    <div className="pgm">
      <div className="pgm__h" aria-hidden="true">
        <span>Ordre</span>
        <span>Page</span>
        {/* ⚠️ AVANT LES TROIS AUTRES, parce qu'elle les commande : fermée, la page n'a
            plus ni entrée, ni tuile, ni public. Les deux coches d'à côté disent où son
            LIEN apparaît ; celle-ci, si elle s'ouvre encore (bug #334). */}
        <span>Ouverte</span>
        <span>Menu</span>
        <span>Accueil</span>
        {/* ⚠️ « PUBLIC », PAS « VISIBLE » : les deux coches d'à côté disent déjà où la
            page s'affiche. Celle-ci dit à QUI elle s'ouvre — quelqu'un qui n'a pas de
            compte. */}
        <span>Public</span>
        {/* La colonne de la croix, sans en-tête : elle ne vaut que pour les pages
            ajoutées, et un mot au-dessus d'une colonne presque vide poserait une
            question à chaque ligne. */}
        <span />
      </div>

      {/* ⚠️ LES ÉTAPES SORTIES DU TUNNEL SONT DES LIGNES COMME LES AUTRES, et à la même
          place que dans le menu — régler la troisième ligne et voir bouger la sixième
          entrée serait le genre de décalage qu'on ne remarque qu'en production.

          ⚠️ ELLES NE DÉPENDENT PAS DE L'INTERRUPTEUR DU PARCOURS. Une étape peut être
          HORS du tunnel et dans le menu : un événement qui n'interroge personne à
          l'inscription mais laisse compléter son profil ensuite est un cas réel. */}
      {/* ⚠️ UNE SEULE LISTE, DANS L'ORDRE DU SITE. Elle était coupée en deux — les
          pages, puis les étapes servies en page — et cette coupure interdisait de les
          entremêler, ce qui est précisément ce qu'on vient régler ici. Ce qu'une page
          EST se lit maintenant à son nom et à son dessin, pas à sa position dans un
          tableau. */}
      {ordered.map((page, i) => line(page, i))}

      {/* Le motif du dernier refus. Il disparaît au premier déplacement qui aboutit :
          une explication qui reste après avoir été comprise se lit comme un reproche. */}
      {refusal && (
        <p className="stm__no" role="status">
          {refusal}
        </p>
      )}

      {/* ⚠️ ON COMPARE À CE QUI EST ENREGISTRÉ, pas à un ordre recalculé : `rangees` EST
          déjà l'ordre d'origine quand rien n'est écrit, donc la comparaison serait
          toujours vraie et le bouton toujours là. */}
      <div className="stm__b">
        {/* ⚠️ UN BOUTON PAR FAMILLE MULTIPLIABLE, engendré : le jour où une troisième
            arrive, elle a le sien sans qu'on y pense. Le mot vient du registre, pas
            d'ici — renommée là-bas, la page renomme son bouton. En minuscules parce
            qu'il est au milieu d'une phrase : « Ajouter une page « Section » » disait
            la même chose en trébuchant dessus. */}
        {REPEATABLE_PAGES.map((base) => (
          <button key={base.key} type="button" onClick={() => addPage(base)}>
            Ajouter {base.article ?? "une"} {base.name.toLowerCase()}
          </button>
        ))}
        {typeof value(PAGES_ORDER_KEY) === "string" &&
          (value(PAGES_ORDER_KEY) as string).trim() !== "" && (
            <button
              type="button"
              onClick={() => {
                setRefusal(null);
                write(PAGES_ORDER_KEY, "");
              }}
            >
              Remettre l'ordre d'origine
            </button>
          )}
      </div>

      {/* ⚠️ DIT UNE FOIS, PAS À CHAQUE CROIX : la phrase du parcours, pour le même
          geste : ce qu'on retire ici ne détruit rien. Le contenu reste en base sous
          ses clés ; remettre une page retrouve le premier rang libre, donc le sien. */}
      <p className="stm__h">
        Retirer une page ajoutée n'efface pas son contenu : il revient avec elle
        si vous la remettez.
      </p>
      {/* ⚠️ CE QUE LES DEUX COCHES NE DISENT PAS : décochée du menu et de l'accueil, une
          page s'ouvrait toujours par son adresse, et rien ici ne le laissait deviner. */}
      <p className="stm__h">
        Sortie du menu et de l'accueil, une page s'ouvre encore par son adresse.
        Décochez « Ouverte » pour la fermer aux participants.
      </p>
    </div>
  );

  /** Une ligne : sa place, son nom, « ouverte », « dans le menu », « en tuile », « public ». */
  function line(page: SitePage, index: number) {
    // L'accueil n'a pas de tuile : elle mènerait à l'écran où l'on se trouve.
    const withoutTile =
      FIXED_PAGES.find((p) => p.key === page.key)?.tile === false;
    const label = name(page);
    /* ⚠️ LE NOM D'UNE ÉTAPE SERVIE EN PAGE EST CELUI DE L'ÉTAPE — `nameKey`. Écrit ici,
       il l'est donc AUSSI dans le parcours : c'est une seule chose. */
    const nameKey = page.nameKey ?? labelKey(page);
    const written = value(nameKey);
    const provenance = detail(nameKey);
    /* Fermée, la page n'apparaît nulle part : les trois coches suivantes sont sans
       effet, et le disent plutôt que de rester cliquables pour rien. Leur état est
       gardé, il revient quand on la rouvre. */
    const closed = isClosed(page, value);
    const moot = closed ? "Sans effet : la page est fermée" : undefined;
    /* ⚠️ MÊME PRÉDICAT QUE `Row` : la traduction n'est offerte que dans la langue de
       référence, sur une valeur écrite non vide, et sous une typologie seulement si
       quelque chose y est écrit — sinon un clic figerait la valeur héritée en surcharge
       de typologie dans toutes les langues. */
    const translatable =
      saving &&
      scope.languageId === scope.defaultLanguageId &&
      typeof provenance.value === "string" &&
      provenance.value.trim().length > 0 &&
      (!scope.typoId || provenance.isSet);
    return (
      <div className="pgm__r" key={page.key}>
        {/* ⚠️ LES DEUX FLÈCHES D'ABORD, comme dans le parcours : c'est le même geste,
            il se trouve au même endroit. Éteintes sur l'accueil et l'inscription, qui
            ouvrent le site et ne se déplacent pas — le motif s'affiche sous la liste
            plutôt que de laisser une flèche muette. */}
        <span className="pgm__o">
          <button
            type="button"
            disabled={!movable(page)}
            aria-label={`Monter ${label}`}
            onClick={() => onMovePage(index, -1)}
          >
            <Icon name="chevron" />
          </button>
          <button
            type="button"
            className="pgm__d"
            disabled={!movable(page)}
            aria-label={`Descendre ${label}`}
            onClick={() => onMovePage(index, 1)}
          >
            <Icon name="chevron" />
          </button>
        </span>
        {/* ⚠️ LE NOM SE RÈGLE ICI, et il ne se réglait NULLE PART : l'aide de la famille
            promettait qu'il « se règle sur la page elle-même », mais aucune portée de
            page ne portait sa ligne. Le repli reste celui du registre — un champ vide
            n'efface pas le nom du menu, il rend la main au produit, et le gris le dit. */}
        <input
          className="pgm__n"
          type="text"
          value={typeof written === "string" ? written : ""}
          placeholder={page.name}
          title={label}
          aria-label={`Nom de la page ${page.name}`}
          onChange={(e) => write(nameKey, e.target.value)}
        />
        {isFixed(page.key) ? (
          /* L'accueil est la racine, l'inscription le tunnel : ni l'une ni l'autre ne se
             ferme d'ici (voir `closedKey`). */
          <span
            className="pgm__x"
            aria-hidden="true"
            title="Sans objet : l'accueil et l'inscription ne se ferment pas"
          >
            —
          </span>
        ) : (
          <button
            type="button"
            className="pgm__c"
            aria-pressed={!closed}
            aria-label={`${label}, ouverte aux participants`}
            title="Ouverte : décochée, la page ne s'ouvre plus aux participants, même par son adresse"
            onClick={() => write(closedKey(page), !closed)}
          >
            <Icon name="check" />
          </button>
        )}
        <button
          type="button"
          className="pgm__c"
          disabled={closed}
          aria-pressed={on(page, navKey(page))}
          aria-label={`${label} — dans le menu`}
          title={moot ?? "Dans le menu"}
          onClick={() => write(navKey(page), !on(page, navKey(page)))}
        >
          <Icon name="check" />
        </button>
        {withoutTile ? (
          /* Une cellule VIDE, pas un interrupteur éteint : « non applicable » et
               « désactivé » ne veulent pas dire la même chose, et un bouton qui ne
               fait rien se clique quand même. */
          <span
            className="pgm__x"
            aria-hidden="true"
            title="Sans objet : c'est l'accueil"
          >
            —
          </span>
        ) : (
          <button
            type="button"
            className="pgm__c"
            disabled={closed}
            aria-pressed={on(page, tileKey(page))}
            aria-label={`${label} — en tuile sur l'accueil`}
            title={moot ?? "En tuile sur l'accueil"}
            onClick={() => write(tileKey(page), !on(page, tileKey(page)))}
          >
            <Icon name="check" />
          </button>
        )}

        {/**
         * ═══ OUVERTE À QUI N'EST PAS CONNECTÉ ═══
         *
         * ⚠️ SEULES LES PAGES QUI SAVENT SE RENDRE SANS SESSION portent l'interrupteur
         * (`SitePage.publishable` : l'accueil et les sections). Les autres lisent la
         * PERSONNE — leur ouvrir la porte les rendrait vides, ce qui se lit comme un
         * site en panne. Une cellule vide plutôt qu'une coche éteinte : « sans objet »
         * et « désactivé » ne disent pas la même chose, et une coche qui ne fait rien
         * se clique quand même.
         */}
        {page.publishable ? (
          <button
            type="button"
            className="pgm__c"
            disabled={closed}
            aria-pressed={value(publicKey(page)) === true}
            aria-label={`${label} — ouverte aux gens non connectés`}
            title={moot ?? "Ouverte aux gens non connectés"}
            onClick={() =>
              write(publicKey(page), value(publicKey(page)) !== true)
            }
          >
            <Icon name="check" />
          </button>
        ) : (
          <span
            className="pgm__x"
            aria-hidden="true"
            title="Sans objet : cette page a besoin de savoir qui la lit"
          >
            —
          </span>
        )}

        {/* Une page AJOUTÉE se retire d'ici ; les pages du produit, elles, s'éteignent
            par leurs deux coches, les retirer de la liste n'aurait pas de sens. La
            cellule reste, vide, pour que la grille garde ses colonnes. */}
        {isAdded(page) ? (
          <button
            type="button"
            className="pgm__k"
            aria-label={`Retirer ${label}`}
            title="Retirer cette page"
            onClick={() => removePage(page.addedTo, page.addedInstance)}
          >
            <Icon name="croix" />
          </button>
        ) : (
          <span aria-hidden="true" />
        )}

        {/* ⚠️ LA PROVENANCE SEULEMENT QUAND ELLE A QUELQUE CHOSE À DIRE. Quatorze
            pastilles « non définie » sous quatorze lignes ne diraient rien et rendraient
            le tableau illisible ; dès qu'un mot est écrit — ici, dans une autre langue,
            pour une typologie — elle revient, avec « reprendre l'héritée » et sa
            traduction. Une clé qui vaut "" n'a rien à dire sur sa provenance, MAIS elle
            masque le niveau du dessous : c'est justement là qu'on doit pouvoir la
            reprendre, d'où les deux conditions. */}
        {(provenance.isSet ||
          (typeof provenance.value === "string" &&
            provenance.value.trim() !== "")) && (
          <span className="pgm__p">
            <Provenance
              settingKey={nameKey}
              translatable={translatable}
              prose
            />
          </span>
        )}
      </div>
    );
  }
}
